import styled from 'styled-components';
import { updateObjectNestedProperties } from '../../../functions/objectFunctions/updateObjectNestedProperties';
import { useScene } from '../../../hooks';
import {
  fontFamilyOptions,
  fontWeightOptions,
  overflowOptions,
} from '../../../types/options';
import { sizeUnits } from '../../../types/units';
import { ColorInput } from '../../common/inputs/ColorInput';
import { PositioningInput } from '../../common/inputs/PositioningInput';
import { UnitInput } from '../../common/inputs/UnitInput';
import { SelectInput } from '../../common/inputs/selectInput/SelectInput';

export const WindowStyleProps = () => {
  const scene = useScene();
  const styleObj: any = scene.launcherWindow.class;

  const labelWidth = 100;

  return (
    <Container>
      <PositioningInput
        key={`window-positioning-input`}
        label="Content Positioning"
        direction={styleObj?.flexDirection || 'row'}
        align={
          styleObj?.flexDirection === 'row'
            ? styleObj?.alignItems
            : styleObj?.justifyContent || 'flex-start'
        }
        justify={
          styleObj?.flexDirection === 'row'
            ? styleObj?.justifyContent
            : styleObj?.alignItems || 'flex-start'
        }
        onChange={(positioning) => {
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.display': 'flex',
              'class.flexDirection': positioning.direction,
              'class.justifyContent':
                positioning.direction === 'row'
                  ? positioning.justify
                  : positioning.align,
              'class.alignItems':
                positioning.direction === 'row'
                  ? positioning.align
                  : positioning.justify,
            })
          );
        }}
      />
      <UnitInput
        id={`window-gap-input`}
        label="Gap"
        value={styleObj?.gap}
        units={sizeUnits}
        defaultUnit="px"
        onChange={(inputValue) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.display': 'flex',
              'class.gap': inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />
      <ColorInput
        id={`window-color-input`}
        label="Background Color"
        value={styleObj?.backgroundColor}
        onChange={(inputValue: string | undefined) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.backgroundColor': inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />
      <UnitInput
        id={`window-padding-input`}
        label="Padding"
        value={styleObj?.padding}
        units={sizeUnits}
        defaultUnit="px"
        onChange={(inputValue) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.boxSizing': 'border-box',
              'class.padding': inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />
      <UnitInput
        id={`window-borerRadius-input`}
        label="Corner Radius"
        value={styleObj?.borderRadius}
        units={sizeUnits}
        defaultUnit="px"
        onChange={(inputValue) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.borderRadius': inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />
      <SelectInput
        id={`window-overflow-input`}
        options={overflowOptions}
        label="Overflow"
        value={styleObj?.overflow}
        onChange={(inputValue: string) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, { 'class.overflow': inputValue })
          )
        }
      />

      {/* Text */}
      <UnitInput
        id={`window-font-size-input`}
        label="Font Size"
        value={styleObj?.fontSize}
        units={sizeUnits}
        defaultUnit="px"
        onChange={(inputValue) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, { 'class.fontSize': inputValue })
          )
        }
        labelWidth={labelWidth}
      />
      <ColorInput
        id={`window-font-color-input`}
        label="Font Color"
        value={styleObj?.color}
        onChange={(inputValue: string | undefined) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, { 'class.color': inputValue })
          )
        }
        labelWidth={labelWidth}
      />
      <SelectInput
        id={`window-font-family-input`}
        options={fontFamilyOptions}
        label="Font Family"
        value={styleObj?.fontFamily}
        onChange={(inputValue: string) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.fontFamily': inputValue,
            })
          )
        }
      />
      <SelectInput
        id={`window-font-weight-input`}
        options={fontWeightOptions}
        label="Font Weight"
        value={styleObj?.fontWeight}
        onChange={(inputValue: string) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.fontWeight': inputValue,
            })
          )
        }
      />

      {/* Scrollbar */}
      <UnitInput
        id={`scrollbar-width-input`}
        label="Scrollbar width"
        value={styleObj?.['::-webkit-scrollbar']?.width}
        units={['px']}
        defaultUnit="px"
        onChange={(inputValue) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.::-webkit-scrollbar.width': inputValue,
              'class.::-webkit-scrollbar.height': inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />
      <UnitInput
        id={`scrollbar-radius-input`}
        label="Scrollbar radius"
        value={styleObj?.['::-webkit-scrollbar-track']?.borderRadius}
        units={['px']}
        defaultUnit="px"
        onChange={(inputValue) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.::-webkit-scrollbar-track.borderRadius': inputValue,
              'class.::-webkit-scrollbar-thumb.borderRadius': inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />
      <ColorInput
        id={`scrollbar-track-color-input`}
        label="Scrollbar Track Color"
        value={styleObj?.['::-webkit-scrollbar-track']?.backgroundColor}
        onChange={(inputValue: string | undefined) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.::-webkit-scrollbar-track.backgroundColor': inputValue,
              'class.::-webkit-scrollbar-corner.background': 'transparent',
            })
          )
        }
        labelWidth={labelWidth}
      />
      <ColorInput
        id={`scrollbar-thumb-color-input`}
        label="Scrollbar Thumb Color"
        value={styleObj?.['::-webkit-scrollbar-thumb']?.backgroundColor}
        onChange={(inputValue: string | undefined) =>
          scene.setLauncherWindow((prev) =>
            updateObjectNestedProperties(prev, {
              'class.::-webkit-scrollbar-thumb.backgroundColor': inputValue,
            })
          )
        }
        labelWidth={labelWidth}
      />

      {/* Transition All */}
      <SelectInput
        id={`transition-all-input`}
        options={[
          { value: undefined, label: '' },
          { value: 'all 0.3s ease', label: 'Ease 0.3s' },
        ]}
        label="Transition All"
        value={styleObj?.['*']?.transition}
        onChange={(inputValue: string) => {
          if (inputValue === undefined) {
            scene.setLauncherWindow((prev) =>
              updateObjectNestedProperties(prev, {
                'class.*': undefined,
              })
            );
          } else {
            scene.setLauncherWindow((prev) =>
              updateObjectNestedProperties(prev, {
                'class.*.transition': inputValue,
              })
            );
          }
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
